import { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';

import { Button } from '@/components/Button';
import { PhoneField } from '@/components/PhoneField/PhoneField';
import { TextField } from '@/components/TextField';
import { PasswordInput } from '@/components/PasswordInput';
import { Checkbox } from '@/components/Checkbox';
import { Divider } from '@/components/Divider';
import { GoogleSignInButton } from '@/components/GoogleSignInButton';
import { PasswordStrength } from '@/components/PasswordStrength';
import { signUp, AuthActionError } from '@/lib/auth-actions';
import { signUpSchema, type SignUpValues } from '@/lib/auth-schemas';
import { useAuthRedirect } from '@/hooks/useAuthRedirect';
import { useRecaptchaToken } from '@/lib/useRecaptchaToken';
import { verifyRecaptcha } from '@/lib/recaptcha';
import { env } from '@/lib/env';

// Signup prefill handed over by the anonymous Interest/Callback nudge
// (ADR-053). The name + email travel through sessionStorage — NOT the URL — so
// no PII lands in history / referrer / logs. Read once and consumed (removed)
// here; only the `redirect` back to the deal stays in the query string.
const SIGNUP_PREFILL_KEY = 'ddm.signupPrefill';

function readSignupPrefill(): { firstName: string; lastName: string; email: string } {
  const empty = { firstName: '', lastName: '', email: '' };
  try {
    const raw = sessionStorage.getItem(SIGNUP_PREFILL_KEY);
    if (!raw) return empty;
    sessionStorage.removeItem(SIGNUP_PREFILL_KEY);
    const parsed = JSON.parse(raw) as Partial<{
      firstName: string;
      lastName: string;
      email: string;
    }>;
    return {
      firstName: typeof parsed.firstName === 'string' ? parsed.firstName : '',
      lastName: typeof parsed.lastName === 'string' ? parsed.lastName : '',
      email: typeof parsed.email === 'string' ? parsed.email : '',
    };
  } catch {
    return empty;
  }
}

export function SignUpPage() {
  const { t } = useTranslation(['auth']);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  // Lazy initializer → runs exactly once, BEFORE useForm defaultValues are
  // captured (a useEffect would leave RHF holding the empty defaults).
  const [prefill] = useState(readSignupPrefill);
  // Carry the post-login `redirect` through the verify flow so a new buyer
  // returns to the originating page after confirming their email.
  const redirectParam = params.get('redirect');

  useAuthRedirect({ mode: 'requireSignedOut', fallback: '/dashboard' });

  const getRecaptchaToken = useRecaptchaToken();

  const {
    register,
    handleSubmit,
    setError,
    watch,
    control,
    formState: { errors },
  } = useForm<SignUpValues>({
    resolver: zodResolver(signUpSchema(t)),
    defaultValues: {
      firstName: prefill.firstName,
      lastName: prefill.lastName,
      email: prefill.email,
      phone: '',
      password: '',
      terms: false as unknown as true,
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true);
    try {
      // reCAPTCHA v3 (Spec V1.1 §M13.6). action='signup' — server-side
      // rate-limit 3/24h/IP (Matrice §3.8). Email omitted: signup is
      // IP-keyed, the user-supplied email is not yet verified.
      const token = await getRecaptchaToken('signup');
      if (!token && env.recaptchaSiteKey) throw new AuthActionError('recaptcha_failed');
      if (token) {
        const verdict = await verifyRecaptcha({ token, action: 'signup' });
        if (!verdict.success) {
          throw new AuthActionError(
            verdict.code === 'rate_limited' ? 'recaptcha_rate_limited' : 'recaptcha_failed',
          );
        }
      }
      await signUp({
        email: values.email,
        password: values.password,
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone || undefined,
      });
      {
        const verifyQuery = new URLSearchParams({ email: values.email });
        if (redirectParam) verifyQuery.set('redirect', redirectParam);
        navigate(`/verify?${verifyQuery.toString()}`, { replace: true });
      }
    } catch (error) {
      if (error instanceof AuthActionError) {
        if (error.code === 'recaptcha_failed') {
          toast.error(t('auth:errors.recaptchaFailed'));
        } else if (error.code === 'recaptcha_rate_limited') {
          toast.error(t('auth:errors.recaptchaRateLimited'));
        } else if (error.code === 'email_already_used') {
          setError('email', { message: t('auth:errors.emailAlreadyUsed') });
        } else if (error.code === 'weak_password') {
          setError('password', { message: t('auth:errors.weakPassword') });
        } else if (error.code === 'rate_limited') {
          toast.error(t('auth:errors.rateLimited'));
        } else if (error.code === 'network') {
          toast.error(t('auth:errors.network'));
        } else {
          toast.error(t('auth:errors.generic'));
        }
      } else {
        toast.error(t('auth:errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <section className="m5-shell">
      <h1 className="m5-shell__title">{t('auth:signUp.title')}</h1>
      <p className="m5-shell__lead">{t('auth:signUp.lead')}</p>

      <div className="m5-card">
        <GoogleSignInButton />
        <Divider label={t('auth:common.or')} />

        <form className="m5-form" onSubmit={onSubmit} noValidate>
          <TextField
            label={t('auth:fields.firstName')}
            autoComplete="given-name"
            required
            error={errors.firstName?.message}
            {...register('firstName')}
          />
          <TextField
            label={t('auth:fields.lastName')}
            autoComplete="family-name"
            required
            error={errors.lastName?.message}
            {...register('lastName')}
          />
          <TextField
            label={t('auth:fields.email')}
            type="email"
            autoComplete="email"
            inputMode="email"
            required
            error={errors.email?.message}
            {...register('email')}
          />
          <Controller
            control={control}
            name="phone"
            render={({ field, fieldState }) => (
              <PhoneField
                name="phone"
                label={t('auth:fields.phone')}
                helper={t('auth:signUp.phoneHelper')}
                value={field.value ?? ''}
                onChange={field.onChange}
                error={fieldState.error?.message}
              />
            )}
          />
          <PasswordInput
            label={t('auth:fields.password')}
            autoComplete="new-password"
            required
            helper={t('auth:signUp.passwordHelper')}
            error={errors.password?.message}
            showLabel={t('auth:common.passwordShow')}
            hideLabel={t('auth:common.passwordHide')}
            {...register('password')}
          />
          <PasswordStrength value={watch('password') ?? ''} />
          <Checkbox
            label={
              <Trans
                i18nKey="auth:signUp.termsLabel"
                components={{
                  terms: <Link to="/legal/terms" className="m5-link" />,
                  privacy: <Link to="/legal/privacy" className="m5-link" />,
                }}
              />
            }
            {...register('terms')}
          />
          {errors.terms?.message ? (
            <span className="m5-field__error" role="alert">
              {errors.terms.message}
            </span>
          ) : null}
          <Button
            type="submit"
            variant="primary"
            loading={submitting}
            loadingLabel={t('auth:common.loading')}
          >
            {t('auth:signUp.submit')}
          </Button>
        </form>

        <p className="m5-footnote">
          {t('auth:signUp.signinPrompt')}{' '}
          <Link to="/signin" className="m5-link">
            {t('auth:signUp.signinLink')}
          </Link>
        </p>

        <p className="m5-recaptcha-notice">
          <Trans
            i18nKey="auth:common.recaptchaNotice"
            components={{
              privacy: (
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="m5-link"
                />
              ),
              terms: (
                <a
                  href="https://policies.google.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="m5-link"
                />
              ),
            }}
          />
        </p>
      </div>
    </section>
  );
}
